import * as Sentry from '@sentry/browser'
import axios, { AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'

import { getVersion } from '@/api/version'
import { useSettingStore } from '@/stores/setting'
import { enhanceError, isErrorData } from '@/utils/errors'
import loc from '@/utils/loc'
import { authRedirect } from '@/utils/utils'

export class UnauthenticatedError extends Error {}
export class ServerFailureError extends Error {}
export class BadRequestError extends Error {}
export class BackendAPIError extends Error {}
export class NoResponseError extends Error {}

function isErrorResponse(response: AxiosResponse): boolean {
  return response.data.error && isErrorData(response.data.error)
}
export async function handleBrowserAxiosError(error) {
  if (!axios.isAxiosError(error)) {
    Sentry.captureException(new Error('Axios had an exception not an AxiosError', { cause: error }))
  } else {
    Sentry.setContext('axiosConfig', error.config)
    Sentry.setContext('axiosRequest', error.request || {})
    if (!error.response) {
      Sentry.captureException(
        new NoResponseError(`Recieved no response in api call to ${error.config.url}`, {
          cause: error,
        }),
      )
    } else {
      const status_code = error.response.status
      Sentry.setContext('axiosResponse', error.response)
      if (!isErrorResponse(error.response)) {
        Sentry.captureException(
          new ServerFailureError(
            `Received ill-formated ${status_code} response from ${error.config.url}`,
            { cause: error },
          ),
        )
      } else {
        Sentry.setContext('ttError', error.response.data.error)

        // Standardize our handling of API responses so code above can easily tell the difference between
        // the BE rejecting an invalid request and something going wrong in typescript code,
        error = enhanceError(error, error.response.data.error)
        if (status_code == 400) {
          Sentry.captureException(
            new BadRequestError(`${error.code} in ${error.config.url}`, { cause: error }),
          )
        } else if (status_code == 401 || status_code == 403) {
          const location = loc.location()
          return authRedirect(location.pathname + location.search)
        } else if (status_code >= 500) {
          Sentry.captureException(
            new ServerFailureError(`Recieved ${status_code} in api call to ${error.config.url}`, {
              cause: error,
            }),
          )
        } else {
          Sentry.captureException(
            new BackendAPIError(`Handled ${status_code}-${error.code} in ${error.config.url}`, {
              cause: error,
            }),
          )
        }
      }
    }
  }
  return Promise.reject(error)
}

export function configureBrowserAxios() {
  axiosRetry(axios, { retries: 2, retryDelay: axiosRetry.exponentialDelay })
  axios.interceptors.response.use(response => {
    useSettingStore().updateVenueAppVersion(response.headers['x-tt-version'])
    return response
  }, handleBrowserAxiosError)

  axios.defaults.xsrfHeaderName = 'X-CSRFToken'
  axios.defaults.xsrfCookieName = 'csrftoken'
  axios.defaults.headers['X-TT-ClientVersion'] = getVersion()
}
